import React from 'react';
import { Trash2, ChevronDown } from 'react-feather';
import { TextField } from '@/shared/components/TextField';
import { Tooltip } from '@shared/components/Tooltip';
import { ServiceTypeInfiniteDropdown } from '@/epics/subscriptionManagment/components/ServiceTypeInfiniteDropdown';
import { StatSummaryCard } from '@/shared/components/StatSummaryCard';
import {
  formatPriceWithSpaces,
  isPriceTooBig,
  MAX_PRICE_LENGTH,
  stripLeadingZeros,
} from '@/epics/subscriptionManagment/hooks/useCreateSubscription';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';

export interface ServiceConfigEntry {
  key: string;
  /** Existing subscription_plan_service_configs row id — present on rows loaded from an existing plan (Edit), absent on freshly-added cards. */
  id?: string;
  serviceTypeId: string;
  serviceName: string;
  usageLimit: string;
  basePrice: string;
  discountPercentage: string;
}

export interface ServiceConfigFieldErrors {
  serviceTypeId?: string;
  usageLimit?: string;
  basePrice?: string;
}

interface ServiceConfigCardProps {
  entry: ServiceConfigEntry;
  expanded: boolean;
  onToggleExpand: () => void;
  onServiceChange: (serviceTypeId: string, serviceName: string) => void;
  onFieldChange: (
    field: 'usageLimit' | 'basePrice' | 'discountPercentage',
    value: string
  ) => void;
  onRemove: () => void;
  excludedServiceTypeIds: string[];
  /** At least one service is required — disable delete when this is the only card left. */
  isOnlyService: boolean;
  errors?: ServiceConfigFieldErrors | undefined;
  disabled?: boolean;
}

/**
 * Per-service accordion card for the Create Subscription Plan "Services" step.
 * Only one card is expanded at a time — expand/collapse state is owned by the
 * parent (useCreateSubscription), not this component, per the PBR rule that
 * at most one service item can be expanded at any point.
 *
 * Matches the Pozytron_Internal Figma "Services" step (node 90513:816529):
 * the service picker lives inside the card (not a separate picker above the
 * list), delete is a trash icon rather than a text link, and there's no
 * per-card "Add" button — completeness is validated on the wizard's "Next".
 */
export const ServiceConfigCard: React.FC<ServiceConfigCardProps> = ({
  entry,
  expanded,
  onToggleExpand,
  onServiceChange,
  onFieldChange,
  onRemove,
  excludedServiceTypeIds,
  isOnlyService,
  errors,
  disabled = false,
}) => {
  // Price fields don't make sense before a service is picked.
  const priceFieldsDisabled = disabled || !entry.serviceTypeId;

  // Usage Limit only means something alongside a discount — it's the
  // number of times the service can be availed at the discounted price
  // before the base price takes over, so with no discount there's nothing
  // for it to gate.
  const hasDiscount = entry.discountPercentage.trim() !== '';
  const usageLimitDisabled = priceFieldsDisabled || !hasDiscount;

  // Preview table needs a service picked and a real base price — discount
  // (and the Usage Limit it gates) is optional and only adds the badge/
  // reduces Final Price/shows a Usage Limit value instead of a dash.
  const basePriceNum = parseFloat(
    entry.basePrice.replace(/\s/g, '').replace(',', '.')
  );
  const canShowPricePreview =
    entry.serviceName.trim() !== '' &&
    !Number.isNaN(basePriceNum) &&
    basePriceNum > 0;

  const discountPercentNum = entry.discountPercentage
    ? Number(entry.discountPercentage)
    : 0;
  const finalPrice = discountPercentNum
    ? basePriceNum * (1 - discountPercentNum / 100)
    : basePriceNum;
  const badgeText = discountPercentNum
    ? `${entry.discountPercentage}% off`
    : undefined;

  const hasCardError = !!(
    errors?.serviceTypeId ||
    errors?.usageLimit ||
    errors?.basePrice
  );

  return (
    <div
      data-field={`service-${entry.key}`}
      className={`flex flex-col gap-2 border rounded-[12px] ${hasCardError ? 'border-error-40' : 'border-primary-color-30'}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={onToggleExpand}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggleExpand();
          }
        }}
        className="flex items-center justify-between px-4 pt-4 cursor-pointer select-none"
      >
        <span className="text-[18px] text-on-surface-variant">
          {SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_SERVICE.polish}
        </span>
        <div className="flex items-center gap-[15px]">
          <Tooltip
            content="At least one service is required. Delete is disabled because this is the last remaining service."
            forceShow={isOnlyService}
            useContentWidth
            contentMaxWidth={130}
            side="bottom"
            triggerWrapContent
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              disabled={disabled || isOnlyService}
              aria-label="Remove service"
              className="text-on-surface-variant cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </Tooltip>
          <ChevronDown
            strokeWidth={1.5}
            aria-hidden="true"
            className={`-mt-0.5 text-on-surface-variant shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      <div
        className={`flex flex-col gap-8 px-4 ${expanded && !canShowPricePreview ? 'pb-[49px]' : 'pb-4'}`}
      >
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2 min-w-0">
            <ServiceTypeInfiniteDropdown
              value={entry.serviceTypeId}
              onChange={onServiceChange}
              excludedIds={excludedServiceTypeIds}
              error={!!errors?.serviceTypeId}
              errorMessage={errors?.serviceTypeId ?? ''}
              disabled={disabled}
              displayName={entry.serviceName}
            />
          </div>
        </div>

        {expanded && (
          <div className="flex flex-col gap-2">
            <div className="border-b border-neutral-73" />

            <p className="text-[18px] text-on-surface-variant mt-6">Price</p>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex flex-col gap-2 min-w-0">
                <TextField
                  label={
                    SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_SERVICE_PRICE.polish
                  }
                  type="amount"
                  maxLength={MAX_PRICE_LENGTH}
                  fullWidth
                  value={formatPriceWithSpaces(entry.basePrice)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\s/g, '');
                    if (isPriceTooBig(raw)) {
                      return;
                    }
                    onFieldChange('basePrice', raw);
                  }}
                  error={!!errors?.basePrice}
                  errorMessage={errors?.basePrice ?? ''}
                  disabled={priceFieldsDisabled}
                  trailingIcon={
                    entry.basePrice ? (
                      <span className="text-bodyLarge text-neutral-10 align-middle ml-[2px] mt-[12px]">
                        zł
                      </span>
                    ) : null
                  }
                />
                {!errors?.basePrice && (
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {
                      SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_ORIGINAL_PRICE_HELPER
                        .polish
                    }
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 min-w-0">
                <TextField
                  label={
                    <>
                      {
                        SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT_PERCENTAGE
                          .polish
                      }{' '}
                      <span style={{ fontSize: 12 }}>
                        {SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_OPTIONAL.polish}
                      </span>
                    </>
                  }
                  type="text"
                  inputMode="decimal"
                  fullWidth
                  value={entry.discountPercentage}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d.]/g, '');
                    // Drop anything from a second decimal point onward, then
                    // cap to 2 decimal places.
                    const segments = raw.split('.');
                    const intSegment = (segments[0] ?? '').replace(
                      /^0+(?=\d)/,
                      ''
                    );
                    let cleaned =
                      segments.length > 1
                        ? `${intSegment}.${segments[1] ?? ''}`
                        : intSegment;
                    const dotIndex = cleaned.indexOf('.');
                    if (dotIndex !== -1) {
                      cleaned = cleaned.slice(0, dotIndex + 3);
                    }
                    if (cleaned === '' || cleaned === '.') {
                      onFieldChange('discountPercentage', '');
                      // Usage Limit only means something alongside a
                      // discount — clear it so a stale value doesn't linger
                      // in the now-disabled field.
                      onFieldChange('usageLimit', '');
                      return;
                    }
                    const num = Number(cleaned);
                    if (!Number.isNaN(num) && num > 100) {
                      onFieldChange('discountPercentage', '100');
                      return;
                    }
                    onFieldChange('discountPercentage', cleaned);
                  }}
                  disabled={priceFieldsDisabled}
                />
                <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                  {SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DEDUCTED_HELPER.polish}
                </p>
              </div>
              <div className="flex flex-col gap-2 min-w-0">
                <TextField
                  label={
                    SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_USAGE_LIMIT.polish
                  }
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={5}
                  fullWidth
                  value={entry.usageLimit}
                  onChange={(e) => {
                    const digitsOnly = e.target.value.replace(/\D/g, '');
                    const stripped = stripLeadingZeros(digitsOnly);
                    // A discount that only ever applies 0 times is
                    // meaningless — block it outright rather than letting it
                    // through and erroring later.
                    if (stripped === '0' && hasDiscount) {
                      return;
                    }
                    onFieldChange('usageLimit', stripped);
                  }}
                  error={!!errors?.usageLimit}
                  errorMessage={errors?.usageLimit ?? ''}
                  disabled={usageLimitDisabled}
                />
                {!errors?.usageLimit && (
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {
                      SUBSCRIPTION_MANAGEMENT_LABELS
                        .FIELD_BASE_PRICE_LIMIT_HELPER.polish
                    }
                  </p>
                )}
              </div>
            </div>

            {canShowPricePreview && (
              <div className="mt-6">
                <StatSummaryCard
                  title={entry.serviceName}
                  badge={
                    badgeText ? (
                      <span className="bg-information-40 text-white text-labelMedium rounded-full px-2 py-0.5">
                        {badgeText}
                      </span>
                    ) : undefined
                  }
                  rows={[
                    [
                      {
                        label:
                          SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_BASE_PRICE
                            .polish,
                        value: `${formatPriceWithSpaces(basePriceNum.toFixed(2))} zł`,
                      },
                      {
                        label:
                          SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_USAGE_LIMIT
                            .polish,
                        value: entry.usageLimit || '—',
                      },
                      {
                        label: 'Final Price',
                        value: `${formatPriceWithSpaces(finalPrice.toFixed(2))} zł`,
                        valueClassName: 'text-labelLarge text-information-30',
                      },
                    ],
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceConfigCard;
